import { api } from './api';

function auth(token: string) {
  return { accessToken: token };
}

/**
 * Service Apporteurs.
 *
 * Endpoints exposés au CRM (cf SECURITY.md sessions 2026-04-27
 * + 2026-05-22). Tous gardés par `ROLE_SETS.writers` côté backend (admin/
 * director/manager/collaborator). Pas de DELETE : la « désactivation » se
 * fait via PATCH `/active` avec `{ isActive: false }`.
 *
 * Modèle polymorphe `type = person | company` (refonte 2026-05-22) :
 *   - person  : firstName + lastName requis
 *   - company : companyName requis
 * `externalId` optionnel partout (sert à reconstruire un mapping legacy → uuid
 * pendant un import depuis l'ancien logiciel).
 */

export type ApporteurType = 'person' | 'company';

export interface ApporteurItem {
  id: string;
  type: ApporteurType;
  /** Identifiant externe (ancien CRM). Optionnel, unique partiel WHERE NOT NULL. */
  externalId: string | null;
  /** person : non-null. company : null. */
  firstName: string | null;
  lastName: string | null;
  /** company : non-null. person : null. */
  companyName: string | null;
  /** Taux de rétrocommission appliqué à la commission (%) en string décimal
   *  serializé (Prisma Decimal). */
  retrocommissionRate: string;
  /** Taux de rétrocommission appliqué aux frais (%) — décision 2026-04-30. */
  retrocommissionRateFees: string;
  /** Taux de rétrocommission appliqué aux honoraires (%) — décision 2026-04-30. */
  retrocommissionRateHonoraires: string;
  /** Pour type=person : employeur. Pour type=company : généralement null. */
  company: string | null;
  phone: string | null;
  email: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  latitude: string | null;
  longitude: string | null;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  creator?: { id: string; firstName: string; lastName: string };
  /** Présent uniquement sur GET /:id - flag UI pour verrouiller l'édition
   *  du taux côté form. `true` si ≥1 opp signée référence l'apporteur. */
  lockedByWonOpps?: boolean;
  /** Présent uniquement sur GET /:id - décompte pour message UX. */
  wonOppsCount?: number;
}

interface ApporteurCommonPayload {
  /** Taux de rétrocommission appliqué à la commission (% 0-100). */
  retrocommissionRate: number;
  /** Taux de rétrocommission appliqué aux frais (% 0-100). Défaut UI = 0. */
  retrocommissionRateFees: number;
  /** Taux de rétrocommission appliqué aux honoraires (% 0-100). Défaut UI = 0. */
  retrocommissionRateHonoraires: number;
  externalId?: string | null;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
}

export type ApporteurCreatePayload =
  | (ApporteurCommonPayload & {
      type: 'person';
      firstName: string;
      lastName: string;
    })
  | (ApporteurCommonPayload & {
      type: 'company';
      companyName: string;
    });

/**
 * PATCH partiel. Le `type` est immuable côté API et silencieusement ignoré
 * (le service ignore aussi les champs cross-type incohérents avec le type
 * réel — ex. firstName sur un apporteur company).
 */
export type ApporteurUpdatePayload = {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  retrocommissionRate?: number;
  retrocommissionRateFees?: number;
  retrocommissionRateHonoraires?: number;
  externalId?: string | null;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
};

export interface ApporteurMapMarker {
  id: string;
  type: ApporteurType;
  /** Nom calculé côté serveur : "Prénom Nom" ou raison sociale. */
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  company: string | null;
  phone: string | null;
  email: string | null;
  latitude: number;
  longitude: number;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  opportunitiesCount: number;
}

export interface ApporteurMapResponse {
  markers: ApporteurMapMarker[];
  totalCount: number;
  truncated: boolean;
  /** Centroid géocodé (filtre rayon) - cf MapResponse côté prospects. */
  geocodedCenter: { latitude: number; longitude: number } | null;
}

export interface ListApporteursFilters {
  is_active?: boolean;
  type?: ApporteurType;
  search?: string;
}

function qs(filters: ListApporteursFilters): string {
  const params = new URLSearchParams();
  if (filters.is_active !== undefined) params.set('is_active', String(filters.is_active));
  if (filters.type !== undefined) params.set('type', filters.type);
  if (filters.search) params.set('search', filters.search);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const apporteursApi = {
  list: (token: string, filters: ListApporteursFilters = {}) =>
    api.get<ApporteurItem[]>(`/api/v1/apporteurs${qs(filters)}`, auth(token)),

  get: (token: string, id: string) =>
    api.get<ApporteurItem>(`/api/v1/apporteurs/${id}`, auth(token)),

  create: (token: string, body: ApporteurCreatePayload) =>
    api.post<ApporteurItem>('/api/v1/apporteurs', body, auth(token)),

  update: (token: string, id: string, body: ApporteurUpdatePayload) =>
    api.patch<ApporteurItem>(`/api/v1/apporteurs/${id}`, body, auth(token)),

  setActive: (token: string, id: string, isActive: boolean) =>
    api.patch<ApporteurItem>(`/api/v1/apporteurs/${id}/active`, { isActive }, auth(token)),

  map: (token: string, filters: { near_location?: string; radius_km?: number } = {}) => {
    const params = new URLSearchParams();
    if (filters.near_location) params.set('near_location', filters.near_location);
    if (filters.radius_km !== undefined) params.set('radius_km', String(filters.radius_km));
    const qs = params.toString();
    return api.get<ApporteurMapResponse>(
      `/api/v1/apporteurs/map${qs ? `?${qs}` : ''}`,
      auth(token),
    );
  },
};

// Helper réexporté pour les call-sites UI (cohérent avec src/shared/apporteur.ts
// côté serveur, mais évite le import depuis `../../shared/...` un peu profond).
export { displayName as apporteurDisplayName } from '../../shared/apporteur';
